import { syncAvatarMenuState } from './authGateController';
import { parseClientPlanContext, type ClientPlanContext } from './clientPlanContext';
import type { PrototypeState } from './controllerState';
import type { CurrentSubscription } from './currentSubscriptionReader';
import type { BillingCycle, Root } from './prototypeTypes';

export type PaywallIdentitySnapshot = {
	generation: number;
	isLoggedIn: true;
	userId: string;
	sessionHash: string;
};

export type PaywallBillingReconcileResult = 'current' | 'reconciled' | 'stale' | 'error';

type SessionReadResult =
	| { kind: 'consistent'; planContext: ClientPlanContext }
	| { kind: 'plan_mismatch' }
	| { kind: 'stale' }
	| { kind: 'error' };

const sessionPropagationRetryMs = 750;

export async function reconcilePaywallBillingState(
	root: Root,
	state: PrototypeState,
	subscription: CurrentSubscription | null,
	identity: PaywallIdentitySnapshot,
	isCurrentRequest: () => boolean,
): Promise<PaywallBillingReconcileResult> {
	if (!ownsIdentity(state, identity, isCurrentRequest)) return 'stale';
	const authoritativeBillingPlan = subscription?.plan ?? 'free';
	if (state.planContext?.billingPlan === authoritativeBillingPlan) return 'current';

	const first = await readConsistentSession(state, subscription, identity, isCurrentRequest);
	if (first.kind === 'stale' || first.kind === 'error') return first.kind;
	if (first.kind === 'consistent') return applyAuthoritativeBillingState(root, state, subscription, first.planContext, identity, isCurrentRequest);

	await new Promise((resolve) => setTimeout(resolve, sessionPropagationRetryMs));
	if (!ownsIdentity(state, identity, isCurrentRequest)) return 'stale';
	const second = await readConsistentSession(state, subscription, identity, isCurrentRequest);
	if (second.kind !== 'consistent') return second.kind === 'stale' ? 'stale' : 'error';
	return applyAuthoritativeBillingState(root, state, subscription, second.planContext, identity, isCurrentRequest);
}

async function readConsistentSession(
	state: PrototypeState,
	subscription: CurrentSubscription | null,
	identity: PaywallIdentitySnapshot,
	isCurrentRequest: () => boolean,
): Promise<SessionReadResult> {
	const getSession = state.runtime.api?.getSession;
	if (typeof getSession !== 'function') return { kind: 'error' };
	let payload: unknown;
	try {
		payload = await getSession.call(state.runtime.api);
	} catch {
		return ownsIdentity(state, identity, isCurrentRequest) ? { kind: 'error' } : { kind: 'stale' };
	}
	if (!ownsIdentity(state, identity, isCurrentRequest)) return { kind: 'stale' };
	if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return { kind: 'error' };
	const viewer = (payload as Record<string, unknown>).viewer;
	if (!viewer || typeof viewer !== 'object' || Array.isArray(viewer)) return { kind: 'error' };
	const record = viewer as Record<string, unknown>;
	if (record.authStatus !== 'authenticated') return { kind: 'stale' };
	if (record.userId !== identity.userId || record.sessionHash !== identity.sessionHash) return { kind: 'stale' };
	const planContext = parseClientPlanContext(record.planContext);
	if (!planContext) return { kind: 'error' };
	const consistent = subscription
		? planContext.billingPlan === subscription.plan
		: planContext.billingPlan === 'free';
	return consistent ? { kind: 'consistent', planContext } : { kind: 'plan_mismatch' };
}

function applyAuthoritativeBillingState(
	root: Root,
	state: PrototypeState,
	subscription: CurrentSubscription | null,
	planContext: ClientPlanContext,
	identity: PaywallIdentitySnapshot,
	isCurrentRequest: () => boolean,
): PaywallBillingReconcileResult {
	if (!ownsIdentity(state, identity, isCurrentRequest)) return 'stale';
	let billingCycle: BillingCycle | null = null;
	if (subscription) {
		if (!isBillingCycle(subscription.billingCycle)) return 'error';
		billingCycle = subscription.billingCycle;
	}
	state.planContext = planContext;
	state.currentPlan = planContext.billingPlan;
	state.effectiveAccessPlan = planContext.effectiveAccessPlan;
	state.runtime.currentPlan = planContext.effectiveAccessPlan;
	if (billingCycle) state.runtime.billingCycle = billingCycle;
	syncAvatarMenuState(root, state);
	return 'reconciled';
}

function ownsIdentity(state: PrototypeState, identity: PaywallIdentitySnapshot, isCurrentRequest: () => boolean): boolean {
	return isCurrentRequest()
		&& state.isLoggedIn === identity.isLoggedIn
		&& state.runtime.userId === identity.userId
		&& state.runtime.sessionHash === identity.sessionHash;
}

function isBillingCycle(value: string | undefined): value is BillingCycle {
	return value === 'monthly' || value === 'quarterly' || value === 'annual';
}
